import { useAtomValue } from "jotai";
import { FC, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { usePartnerLogin } from "@/apps/partner/hooks/usePartnerLogin";
import { Button } from "@/components/Interface/Button/Button";
import { TextField } from "@/components/Interface/TextField/TextFields";
import { useAlert } from "@/hooks/useAlert";
import { accessTokenAtom, refreshTokenAtom } from "@/utils/atoms";
import { BACKEND_EMAIL_REGEX } from "@/utils/constants";
import { createLoginErrorHandler } from "@/utils/loginErrorHandler";

export type PartnerLoginFormFields = {
  emailAddress: string;
  password: string;
};

export const PartnerLoginForm: FC = () => {
  const {
    handleSubmit,
    register,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<PartnerLoginFormFields>({
    defaultValues: {
      emailAddress: "",
      password: "",
    },
  });
  const { t } = useTranslation();
  const showAlert = useAlert();
  const accessToken = useAtomValue(accessTokenAtom);
  const refreshToken = useAtomValue(refreshTokenAtom);

  const { isLoading: publicPartnerLoading, loginAsPublicPartner } =
    usePartnerLogin({
      onSuccess: () => {},
      onError: (error) => {
        console.error(error);
      },
    });

  const {
    error,
    isLoading: partnerLoginLoading,
    loginAsPartner,
  } = usePartnerLogin({
    onError: (error) => {
      // Create error handler with current 2FA modal state
      const errorHandler = createLoginErrorHandler(
        setError,
        t,
        showAlert,
        "emailAddress",
      );
      errorHandler(error);
    },
  });

  const attemptedPublicLoginRef = useRef(false);
  useEffect(() => {
    // Only call loginAsPublicPartner if there's no access token and no refresh token
    // This prevents overriding an existing partner session
    if (!accessToken && !refreshToken && !attemptedPublicLoginRef.current) {
      attemptedPublicLoginRef.current = true;
      loginAsPublicPartner();
    }
  }, [accessToken, refreshToken, loginAsPublicPartner]);

  const onSubmit = async (data: PartnerLoginFormFields) => {
    await loginAsPartner({
      email: data.emailAddress,
      password: data.password,
    });
  };

  return (
    <form
      className="max-w-[420px] md:m-auto md:h-max md:w-full"
      onSubmit={handleSubmit(onSubmit)}
    >
      <h1 className="mb-10 text-4xl font-semibold text-white">
        {t("partnerPortal:welcomeScreen.title")}
      </h1>

      <TextField
        id="emailAddress"
        error={errors.emailAddress}
        inputType="email"
        labelText={t("translation:Common.generalForm.fields.email.label")}
        placeholder={t(
          "translation:Common.generalForm.fields.email.placeholder",
        )}
        register={register("emailAddress", {
          required: t(
            "translation:Common.generalForm.validation.emailRequired",
          ),
          pattern: {
            value: BACKEND_EMAIL_REGEX,
            message: t(
              "translation:Common.generalForm.validation.emailNotValid",
            ),
          },
        })}
      />
      <TextField
        id="password"
        error={errors.password}
        inputType="password"
        labelText={t("translation:Common.generalForm.fields.password.label")}
        placeholder={t(
          "translation:Common.generalForm.fields.password.placeholder",
        )}
        register={register("password", {
          required: t(
            "translation:Common.generalForm.validation.passwordRequired",
          ),
        })}
      />

      <Button
        fullWidth
        className="accent mb-4"
        type="submit"
        disabled={publicPartnerLoading || partnerLoginLoading || isSubmitting}
      >
        {t("translation:newWelcome.logInForm.submitButton")}
      </Button>
    </form>
  );
};
